import { t } from '@lingui/macro'
import { motion } from 'framer-motion'
import { AlertTriangle, CheckCircle2, Clock, XCircle } from 'lucide-react'
import React from 'react'
export type StatusCategory = {
    label: string
    count: number
    percentage: number
    color: 'chart-success' | 'chart-attention' | 'chart-failed' | 'chart-neutral'
}

const iconMap: Record<string, React.ElementType> = {
    Successful: CheckCircle2,
    'Needs Attention': AlertTriangle,
    Failed: XCircle,
    'Not Started': Clock
}

const colorClassMap: Record<string, { bg: string; text: string; bar: string }> = {
    'chart-success': {
        bg: 'bg-om1-green/10',
        text: 'text-om1-green',
        bar: 'bg-om1-green'
    },
    'chart-attention': {
        bg: 'bg-om1-amber/10',
        text: 'text-om1-amber',
        bar: 'bg-om1-amber'
    },
    'chart-failed': {
        bg: 'bg-om1-red/10',
        text: 'text-om1-red',
        bar: 'bg-om1-red'
    },
    'chart-neutral': {
        bg: 'bg-muted',
        text: 'text-muted-foreground',
        bar: 'bg-muted-foreground/50'
    }
}

const barColorMap: Record<string, string> = {
    'chart-success': '#22c55e',
    'chart-attention': '#f59e0b',
    'chart-failed': '#ef4444',
    'chart-neutral': '#9ca3af'
}

/* eslint-disable string-to-lingui/missing-lingui-transformation */
const iconColorMap: Record<string, { bg: string; text: string }> = {
    'chart-success': { bg: 'rgba(34, 197, 94, 0.12)', text: '#22c55e' },
    'chart-attention': { bg: 'rgba(245, 158, 11, 0.12)', text: '#f59e0b' },
    'chart-failed': { bg: 'rgba(239, 68, 68, 0.12)', text: '#ef4444' },
    'chart-neutral': { bg: 'rgba(156, 163, 175, 0.2)', text: '#6b7280' }
}
/* eslint-enable string-to-lingui/missing-lingui-transformation */

const StatusWidget = ({ statusCategories }: { statusCategories: StatusCategory[] }) => {
    const total = statusCategories.reduce((sum, c) => sum + c.count, 0)

    return (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.4 }}>
            <h3 className='text-sm font-semibold text-card-foreground mb-5'>{t`Processing Status`}</h3>

            <div className='flex rounded-full h-3 overflow-hidden mb-6'>
                {statusCategories.map((cat) => (
                    <div
                        key={cat.label}
                        className={`${colorClassMap[cat.color].bar} transition-all`}
                        style={{
                            width: `${cat.percentage}%`,
                            backgroundColor: barColorMap[cat.color]
                        }}
                    />
                ))}
            </div>

            <div className='grid grid-cols-2 gap-4'>
                {statusCategories.map((cat) => {
                    const Icon = iconMap[cat.label]
                    const iconColors = iconColorMap[cat.color]
                    return (
                        <div key={cat.label} className='flex items-start gap-3'>
                            <div className='p-2 rounded-lg' style={{ backgroundColor: iconColors.bg }}>
                                <Icon size={16} style={{ color: iconColors.text }} />
                            </div>
                            <div>
                                <p className='text-xs text-muted-foreground'>{cat.label}</p>
                                <p className='text-lg font-bold text-card-foreground leading-tight'>{cat.count.toLocaleString()}</p>
                                <p className='text-xs font-medium' style={{ color: iconColors.text }}>
                                    {cat.percentage}%
                                </p>
                            </div>
                        </div>
                    )
                })}
            </div>
            {total === 0 && <p className='text-xs text-muted-foreground mt-4'>{t`No status data available`}</p>}
        </motion.div>
    )
}

export default StatusWidget
